import * as React from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

// Generate 48 time slots: 00:00, 00:30, ... 23:30
const TIME_SLOTS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  required?: boolean;
  /** HH:MM string — slots before this time are disabled in the dropdown */
  minTime?: string;
  disabled?: boolean;
}

export const TimeInput = React.forwardRef<HTMLDivElement, TimeInputProps>(
  function TimeInput({ value, onChange, className, required, minTime, disabled }, ref) {
  const [displayValue, setDisplayValue] = React.useState(value || "");
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setDisplayValue(value || "");
  }, [value]);

  const formatTimeInput = (input: string): string => {
    const cleaned = input.replace(/[^\d:]/g, "");
    const digits = cleaned.replace(/:/g, "");
    if (digits.length === 0) return "";
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) {
      const hours = digits.slice(0, 2);
      const minutes = digits.slice(2);
      return `${hours}:${minutes}`;
    }
    const hours = digits.slice(0, 2);
    const minutes = digits.slice(2, 4);
    return `${hours}:${minutes}`;
  };

  const validateAndNormalize = (input: string): string | null => {
    const formatted = formatTimeInput(input);
    if (!formatted) return "";
    const match = formatted.match(/^(\d{1,2}):?(\d{0,2})$/);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    let minutes = match[2] ? parseInt(match[2], 10) : 0;
    if (hours > 23) hours = 23;
    if (minutes > 59) minutes = 59;
    if (formatted.includes(":") && match[2].length === 2) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    return null;
  };

  const clampToMin = (time: string): string => {
    if (!minTime || minTime.length < 5 || time.length < 5) return time;
    return time < minTime ? minTime : time;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const formatted = formatTimeInput(raw);
    setDisplayValue(formatted);
    const normalized = validateAndNormalize(raw);
    if (normalized !== null && normalized.length === 5) {
      const clamped = clampToMin(normalized);
      setDisplayValue(clamped);
      onChange(clamped);
    }
  };

  const handleBlur = () => {
    if (!displayValue) {
      onChange("");
      return;
    }
    const digits = displayValue.replace(/:/g, "");
    let normalized = "";
    if (digits.length >= 3) {
      let hours = parseInt(digits.slice(0, 2), 10);
      let minutes = parseInt(digits.slice(2, 4) || "0", 10);
      if (hours > 23) hours = 23;
      if (minutes > 59) minutes = 59;
      normalized = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    } else if (digits.length === 2) {
      let hours = parseInt(digits, 10);
      if (hours > 23) hours = 23;
      normalized = `${String(hours).padStart(2, "0")}:00`;
    } else if (digits.length === 1) {
      const hours = parseInt(digits, 10);
      normalized = `${String(hours).padStart(2, "0")}:00`;
    }
    if (normalized) {
      const clamped = clampToMin(normalized);
      setDisplayValue(clamped);
      onChange(clamped);
    }
  };

  const handleSlotSelect = (slot: string) => {
    const clamped = clampToMin(slot);
    setDisplayValue(clamped);
    onChange(clamped);
    setOpen(false);
  };

  const isSlotDisabled = (slot: string): boolean => {
    if (!minTime || minTime.length < 5) return false;
    return slot < minTime;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={ref} className={cn("relative", className)}>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="HH:MM"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="w-full font-mono pr-9"
            required={required}
            disabled={disabled}
            maxLength={5}
          />
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              disabled={disabled}
            >
              <Clock className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        align="start"
        sideOffset={2}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col max-h-[min(13rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain">
          {TIME_SLOTS.filter((slot) => !isSlotDisabled(slot)).map((slot) => (
            <button
              key={slot}
              type="button"
              className={cn(
                "px-3 py-1.5 text-sm text-left rounded-sm font-mono transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                slot === displayValue && "bg-accent text-accent-foreground font-semibold"
              )}
              onClick={() => handleSlotSelect(slot)}
            >
              {slot}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});
